import React, { useEffect, useState, useCallback, useMemo } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, Badge } from "../../components/ui-extras";
import { CheckCircle, XCircle } from "@phosphor-icons/react";
import { Can } from "../../lib/rbac";

function _fmtIso(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (_e) {
    return iso;
  }
}

const FIELD_LABEL = {
  driver_name: "Driver",
  unit: "Unit #",
  phone: "Phone #",
  pin: "PIN #",
  status: "Status",
};

// Minutes-since-submitted → "1h 23m" / "48m" / "2d 3h". Blank string
// for a null/unparseable input so the caller can decide what to render.
function _fmtAge(iso) {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const min = Math.max(0, Math.floor((Date.now() - t) / 60000));
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h < 24) return m ? `${h}h ${m}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh ? `${d}d ${rh}h` : `${d}d`;
}

// Ops-relevant threshold (matches the CCR digest's default of >1 hour).
// Anything older lights up amber so ops can spot overdue rows in the
// queue at a glance — no waiting for the nightly digest.
function _isOverdue(iso, minutes = 60) {
  if (!iso) return false;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return false;
  return (Date.now() - t) / 60000 > minutes;
}

function StatusBadge({ status }) {
  if (status === "approved") return <Badge variant="success">approved</Badge>;
  if (status === "rejected") return <Badge variant="error">rejected</Badge>;
  return <Badge variant="info">pending</Badge>;
}

function ReviewDialog({ request, action, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const path = `/admin/card-change-requests/${request.request_id}/${action}`;
      await api.post(path, { admin_note: note.trim() || null });
      toast.success(`Request ${action}d.`);
      if (onDone) onDone();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not ${action} request.`);
    } finally {
      setBusy(false);
    }
  };

  const verb = action === "approve" ? "Approve" : "Reject";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        className="bg-white w-full max-w-lg rounded shadow-lg border border-zinc-200"
        data-testid={`admin-ccr-${action}-dialog`}
      >
        <div className="px-5 py-3 border-b border-zinc-100">
          <div className="overline text-[10px]">{verb.toUpperCase()} REQUEST</div>
          <div className="text-sm font-medium">
            {request.customer_name || request.customer_id} · ····{request.card_last5} ·{" "}
            {FIELD_LABEL[request.field_name] || request.field_name}
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            <span className="text-zinc-400">{request.old_value || "—"}</span>{" "}
            <span className="text-zinc-300">→</span>{" "}
            <span className="text-zinc-900 font-medium">{request.new_value}</span>
          </div>
        </div>
        <div className="px-5 py-4 space-y-3">
          <label className="overline text-[10px]">ADMIN NOTE (OPTIONAL)</label>
          <textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="gf-input w-full"
            placeholder={action === "reject"
              ? "Reason shown to the customer…"
              : "Internal note…"}
            data-testid={`admin-ccr-${action}-note`}
          />
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={onClose}
              className="gf-btn gf-btn-outline"
              data-testid={`admin-ccr-${action}-cancel`}
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={busy}
              className={`gf-btn ${action === "reject" ? "gf-btn-danger" : "gf-btn-primary"}`}
              data-testid={`admin-ccr-${action}-confirm`}
            >
              {busy ? "Saving…" : verb}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminCardChangeRequests() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [cardSearch, setCardSearch] = useState("");
  const [ageSort, setAgeSort] = useState(null); // null | "desc" | "asc"
  const [reviewing, setReviewing] = useState(null); // {request, action}
  // Live-tick for the PENDING FOR column so ages advance while an
  // operator sits on the page. 60s tick is cheap — pure re-render.
  const [, setNowTick] = useState(0);

  const reload = useCallback(() => {
    setLoading(true);
    const params = {};
    if (statusFilter) params.status = statusFilter;
    const q = cardSearch.replace(/\D/g, "").slice(-5);
    if (q) params.card_last5 = q;
    api.get("/admin/card-change-requests", { params })
      .then(({ data }) => setRows(data || []))
      .finally(() => setLoading(false));
  }, [statusFilter, cardSearch]);

  useEffect(reload, [reload]);

  // Auto-refresh — pull fresh data every 10 minutes so new customer
  // submissions surface without a manual refresh, and per-row ages
  // stay honest for a long-lived tab. Pauses while a review dialog
  // is open to avoid yanking the row out from under the operator.
  useEffect(() => {
    if (reviewing) return undefined;
    const id = setInterval(reload, 10 * 60 * 1000);
    return () => clearInterval(id);
  }, [reload, reviewing]);

  // Cheaper local tick — re-render every 60s so the age string
  // (`15m` → `16m` → …) advances between reloads.
  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const displayRows = useMemo(() => {
    if (!ageSort) return rows;
    // "Older" == earlier requested_at == higher age.
    // desc = oldest first (highest age at top); asc = newest first.
    const sign = ageSort === "desc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const ta = Date.parse(a.requested_at) || 0;
      const tb = Date.parse(b.requested_at) || 0;
      return sign * (ta - tb);
    });
  }, [rows, ageSort]);

  const toggleAgeSort = () => {
    setAgeSort((prev) => (
      prev === "desc" ? "asc" : (prev === "asc" ? null : "desc")
    ));
  };
  const ageSortIndicator = ageSort === "desc" ? " ↓" : ageSort === "asc" ? " ↑" : "";

  return (
    <>
      <PageHeader
        title="Card change requests"
        subtitle="ADMIN / CARD CHANGE REQUESTS"
        testId="admin-ccr-header"
      />

      <div className="gf-card mb-4">
        <div className="gf-card-body flex items-center justify-between gap-3">
          <div className="text-sm text-zinc-600">
            Review customer-submitted change requests for Driver Name, Unit #,
            Phone #, PIN # and Status. Approving with an effective date creates
            an append-only card history event and applies today's changes to
            the card. Rejecting leaves the card untouched.
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={cardSearch}
              onChange={(e) => setCardSearch(e.target.value)}
              placeholder="Card ending…"
              className="gf-input"
              style={{ width: 140 }}
              inputMode="numeric"
              maxLength={5}
              data-testid="admin-ccr-card-search"
            />
            {cardSearch && (
              <button
                type="button"
                className="text-xs text-zinc-500 hover:text-zinc-800"
                onClick={() => setCardSearch("")}
                data-testid="admin-ccr-card-search-clear"
              >
                ✕
              </button>
            )}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="gf-input"
              data-testid="admin-ccr-status-filter"
            >
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="">All</option>
            </select>
          </div>
        </div>
      </div>

      <div className="gf-card">
        <table className="gf-table" data-testid="admin-ccr-table">
          <thead>
            <tr>
              <th>SUBMITTED</th>
              <th
                onClick={toggleAgeSort}
                className="cursor-pointer select-none hover:text-zinc-900"
                title="Click to sort by pending age. Cycles: oldest first → newest first → default"
                data-testid="admin-ccr-age-sort"
              >
                PENDING FOR{ageSortIndicator}
              </th>
              <th>CUSTOMER</th>
              <th>CARD</th>
              <th>FIELD</th>
              <th>FROM → TO</th>
              <th>EFFECTIVE</th>
              <th>STATUS</th>
              <th>REVIEWER</th>
              <th>NOTE</th>
              <th className="text-right">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={11} className="text-center py-10 text-zinc-400">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td
                  colSpan={11}
                  className="text-center py-10 text-zinc-400"
                  data-testid="admin-ccr-empty"
                >
                  Nothing to review.
                </td>
              </tr>
            )}
            {displayRows.map((r) => (
              <tr
                key={r.request_id}
                data-testid={`admin-ccr-row-${r.request_id}`}
              >
                <td className="font-mono text-xs">{_fmtIso(r.requested_at)}</td>
                <td
                  className={
                    "font-mono text-xs " +
                    (r.status === "pending" && _isOverdue(r.requested_at)
                      ? "text-amber-700 font-semibold"
                      : "text-zinc-500")
                  }
                  data-testid={`admin-ccr-age-${r.request_id}`}
                  title={
                    r.status === "pending"
                      ? "Time since the customer submitted this request"
                      : "Resolved"
                  }
                >
                  {r.status === "pending" ? (_fmtAge(r.requested_at) || "—") : "—"}
                </td>
                <td className="text-sm">{r.customer_name || r.customer_id}</td>
                <td className="font-mono text-xs">····{r.card_last5}</td>
                <td>
                  <Badge variant="info">
                    {FIELD_LABEL[r.field_name] || r.field_name}
                  </Badge>
                </td>
                <td className="text-xs">
                  <span className="text-zinc-500">{r.old_value || "—"}</span>
                  <span className="mx-1 text-zinc-300">→</span>
                  <span className="text-zinc-900 font-medium">{r.new_value}</span>
                </td>
                <td
                  className="font-mono text-xs text-zinc-700"
                  data-testid={`admin-ccr-effective-${r.request_id}`}
                >
                  {r.effective_date || "—"}
                  {r.effective_date && r.effective_date < new Date().toISOString().slice(0, 10) && (
                    <Badge
                      variant="warn"
                      testId={`admin-ccr-backdated-${r.request_id}`}
                    >
                      BACKDATED
                    </Badge>
                  )}
                </td>
                <td>
                  <div className="flex items-center gap-1.5">
                    <StatusBadge status={r.status} />
                    {r.status === "pending" && _isOverdue(r.requested_at) && (
                      <Badge
                        variant="error"
                        testId={`admin-ccr-overdue-${r.request_id}`}
                      >
                        overdue
                      </Badge>
                    )}
                  </div>
                </td>
                <td className="font-mono text-xs">
                  {r.reviewed_by || "—"}<br />
                  <span className="text-zinc-400">
                    {r.reviewed_at ? _fmtIso(r.reviewed_at) : ""}
                  </span>
                </td>
                <td
                  className="text-xs max-w-[180px] break-words text-zinc-600"
                  data-testid={`admin-ccr-note-${r.request_id}`}
                >
                  {r.admin_note || "—"}
                </td>
                <td className="text-right">
                  {r.status === "pending" ? (
                    <Can perm="card_change_requests.approve" fallback={<span className="text-zinc-400 text-xs">view only</span>}>
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => setReviewing({ request: r, action: "approve" })}
                        className="gf-btn gf-btn-outline text-xs"
                        data-testid={`admin-ccr-approve-${r.request_id}`}
                      >
                        <CheckCircle size={12} weight="bold" /> Approve
                      </button>
                      <button
                        onClick={() => setReviewing({ request: r, action: "reject" })}
                        className="gf-btn gf-btn-outline text-xs text-rose-700"
                        data-testid={`admin-ccr-reject-${r.request_id}`}
                      >
                        <XCircle size={12} weight="bold" /> Reject
                      </button>
                    </div>
                    </Can>
                  ) : (
                    <span className="text-zinc-400 text-xs">closed</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {reviewing && (
        <ReviewDialog
          request={reviewing.request}
          action={reviewing.action}
          onClose={() => setReviewing(null)}
          onDone={reload}
        />
      )}
    </>
  );
}
